"use client";

import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Mail, Phone, MessageSquare, Calendar } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import type { Communications } from "@/src/gql/graphql";
import {
  getCommunicationEmailSourceBadgeClass,
  parseCommunicationEmailProvenance,
} from "@/lib/utils/crm/communication-email-provenance";

interface CommunicationsInfoSummaryProps {
  item: Communications;
}

/**
 * Communications Info Summary Component
 * Displays key information about a communication in a compact format
 * Used in the detail modal header for quick reference
 */
export function CommunicationsInfoSummary({ item }: CommunicationsInfoSummaryProps) {
  const communicationWithExtended = item as unknown as any;
  const emailProvenance = parseCommunicationEmailProvenance(communicationWithExtended.metadata);
  
  // Determine icon based on direction
  const getDirectionIcon = () => {
    const direction = communicationWithExtended.direction?.toLowerCase();
    switch (direction) {
      case "inbound":
        return <Mail className="w-4 h-4" />;
      case "outbound":
        return <Phone className="w-4 h-4" />;
      case "internal":
        return <MessageSquare className="w-4 h-4" />;
      default:
        return <Mail className="w-4 h-4" />;
    }
  };

  // Get status color
  const getStatusColor = () => {
    const status = communicationWithExtended.status?.toLowerCase();
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200";
      case "pending":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200";
      case "failed":
        return "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200";
    }
  };

  return (
    <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/40 dark:to-indigo-950/40 border-blue-200 dark:border-blue-800">
      <CardContent className="pt-4">
        <div className="space-y-3">
          {/* Direction and Status Row */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm">
              {getDirectionIcon()}
              <span className="font-medium text-gray-700 dark:text-gray-300">
                {communicationWithExtended.direction || "Unknown"}
              </span>
            </div>
            <Badge className={`${getStatusColor()} border-0`}>
              {communicationWithExtended.status || "Unknown"}
            </Badge>
            {emailProvenance.sourceLabel ? (
              <Badge
                variant="outline"
                className={`border ${getCommunicationEmailSourceBadgeClass(emailProvenance.source)}`}
              >
                {emailProvenance.sourceLabel}
              </Badge>
            ) : null}
          </div>

          {/* Type and Occurred At Row */}
          <div className="flex items-center justify-between text-sm">
            <div className="text-gray-600 dark:text-gray-400">
              <span className="font-medium">Type:</span> {communicationWithExtended.type_label || communicationWithExtended.type_id || "Unknown"}
            </div>
            {communicationWithExtended.occurred_at && (
              <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                <Calendar className="w-4 h-4" />
                <span>{formatDistanceToNow(new Date(communicationWithExtended.occurred_at), { addSuffix: true })}</span>
              </div>
            )}
          </div>

          {emailProvenance.isEmailSync ? (
            <div className="grid grid-cols-1 gap-1 text-sm text-gray-700 dark:text-gray-300">
              {emailProvenance.providerLabel ? (
                <div>
                  <span className="font-medium">Sync:</span> {emailProvenance.providerLabel}
                  {emailProvenance.syncedMailbox ? ` · ${emailProvenance.syncedMailbox}` : ""}
                </div>
              ) : null}
              {emailProvenance.contactSelectorEmail ? (
                <div>
                  <span className="font-medium">Selector contact:</span>{" "}
                  {emailProvenance.contactSelectorEmail}
                </div>
              ) : null}
            </div>
          ) : null}

          {/* Contact and Company Row */}
          <div className="flex items-center justify-between text-sm">
            {communicationWithExtended.contact_name && (
              <div className="text-gray-700 dark:text-gray-300">
                <span className="font-medium">Contact:</span> {communicationWithExtended.contact_name}
              </div>
            )}
            {communicationWithExtended.company_name && (
              <div className="text-gray-700 dark:text-gray-300">
                <span className="font-medium">Company:</span> {communicationWithExtended.company_name}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
